(() => {
  const SELECTOR = "[data-browse-collection-links]";
  let collectionsIndexPromise = null;

  function fetchAllCollections() {
    if (collectionsIndexPromise) return collectionsIndexPromise;
    collectionsIndexPromise = (async () => {
      const byId = new Map();
      let page = 1;
      while (page <= 10) {
        try {
          const res = await fetch(`/collections.json?limit=250&page=${page}`, {
            credentials: "same-origin",
          });
          if (!res.ok) break;
          const data = await res.json();
          const list = Array.isArray(data?.collections) ? data.collections : [];
          list.forEach((c) => {
            if (c && c.id) byId.set(String(c.id), c);
          });
          if (list.length < 250) break;
          page += 1;
        } catch (_) {
          break;
        }
      }
      return byId;
    })();
    return collectionsIndexPromise;
  }

  function parseGids(raw) {
    if (typeof raw !== "string") return [];
    const cleaned = raw.replace(/[\[\]"]/g, "").trim();
    if (!cleaned) return [];
    return cleaned
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
  }

  function gidToId(gid) {
    const parts = gid.split("/");
    return parts[parts.length - 1] || "";
  }

  function normalizeName(value) {
    return String(value || "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, " ");
  }

  function resolveCollectionByPageName(byId, pageName) {
    const normalized = normalizeName(pageName);
    if (!normalized) return null;

    let exact = null;
    byId.forEach((coll) => {
      if (exact || !coll) return;
      const title = normalizeName(coll.title);
      if (title === normalized) {
        exact = coll;
      }
    });
    return exact;
  }

  function updateButtonText(btn, expanded) {
    const more = btn.getAttribute("data-view-more-label") || "View More";
    const less = btn.getAttribute("data-view-less-label") || "View Less";
    btn.textContent = expanded ? less : more;
    btn.setAttribute("aria-expanded", expanded ? "true" : "false");
  }

  function setExpanded(root, expanded) {
    root.dataset.expanded = expanded ? "true" : "false";
    const items = root.querySelectorAll("[data-browse-collection-item]");
    const maxVisible = Number(root.getAttribute("data-max-visible") || "0");
    items.forEach((el, idx) => {
      if (expanded) {
        el.hidden = false;
        return;
      }
      el.hidden = maxVisible > 0 ? idx >= maxVisible : false;
    });
    const btn = root.querySelector("[data-browse-collection-toggle]");
    if (btn) updateButtonText(btn, expanded);
  }

  async function init(root) {
    if (!root || root.dataset.browseInit === "true") return;
    root.dataset.browseInit = "true";

    const jsonEl = root.querySelector("[data-browse-entries]");
    const grid = root.querySelector("[data-browse-grid]");
    const emptyMsg = root.querySelector("[data-browse-empty]");
    const btn = root.querySelector("[data-browse-collection-toggle]");
    const resolverStatus = root.querySelector("[data-coveo-resolver-status]");
    const coveoPageName = root.getAttribute("data-coveo-page-name") || "";
    const hideIfEmpty = root.getAttribute("data-hide-if-empty") === "true";

    if (!jsonEl || !grid) return;

    /** @type {{ resolved: unknown[]; fallback: unknown[] }} */
    let payload = { resolved: [], fallback: [] };
    try {
      const raw = JSON.parse(jsonEl.textContent || "{}");
      if (Array.isArray(raw)) {
        payload = { resolved: [], fallback: raw };
      } else {
        payload = {
          resolved: Array.isArray(raw.resolved) ? raw.resolved : [],
          fallback: Array.isArray(raw.fallback) ? raw.fallback : [],
        };
      }
    } catch (_) {
      payload = { resolved: [], fallback: [] };
    }

    const resolved = payload.resolved;
    const fallbackEntries = payload.fallback;
    const hasResolved = Array.isArray(resolved) && resolved.length > 0;
    const hasFallback = Array.isArray(fallbackEntries) && fallbackEntries.length > 0;

    if (!hasResolved && !hasFallback) {
      if (hideIfEmpty) {
        root.style.display = "none";
      } else if (emptyMsg) {
        emptyMsg.style.display = "";
      }
      return;
    }

    const useFallback = !hasResolved && hasFallback;
    const needCollectionsIndex =
      useFallback || Boolean(resolverStatus && coveoPageName.trim());

    const byId = needCollectionsIndex ? await fetchAllCollections() : new Map();

    if (resolverStatus && coveoPageName.trim()) {
      const matched = resolveCollectionByPageName(byId, coveoPageName);
      if (matched && matched.handle) {
        root.setAttribute("data-coveo-collection-handle", matched.handle);
        resolverStatus.dataset.status = "ok";
        resolverStatus.textContent =
          `Coveo Page Name "${coveoPageName}" resolved to handle "${matched.handle}" ` +
          `(query key: ec_collection_handle=="${matched.handle}").`;
      } else {
        root.removeAttribute("data-coveo-collection-handle");
        resolverStatus.dataset.status = "error";
        resolverStatus.textContent =
          `No Shopify collection matched Coveo Page Name "${coveoPageName}". ` +
          "Use the exact page/collection title.";
      }
    }

    const links = [];

    if (hasResolved) {
      [...resolved]
        .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
        .forEach((entry) => {
          if (!entry || !entry.handle) return;
          const label = entry.label || entry.handle;
          links.push({
            url: `/collections/${entry.handle}`,
            label: String(label || entry.handle),
          });
        });
    } else {
      [...fallbackEntries]
        .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
        .forEach((entry) => {
          if (!entry) return;
          const gids = parseGids(entry.gids_raw);
          gids.forEach((gid) => {
            const id = gidToId(gid);
            const coll = byId.get(id);
            if (!coll || !coll.handle) return;
            links.push({
              url: `/collections/${coll.handle}`,
              label: entry.label_override || coll.title || coll.handle,
            });
          });
        });
    }

    if (!links.length) {
      if (hideIfEmpty) {
        root.style.display = "none";
      } else if (emptyMsg) {
        emptyMsg.style.display = "";
      }
      return;
    }

    links.forEach((link) => {
      const li = document.createElement("li");
      li.className = "browse-collection-links__item";
      li.setAttribute("data-browse-collection-item", "");
      const a = document.createElement("a");
      a.className = "browse-collection-links__link";
      a.href = link.url;
      a.textContent = link.label;
      li.appendChild(a);
      grid.appendChild(li);
    });

    const maxVisible = Number(root.getAttribute("data-max-visible") || "0");
    const alwaysShow = root.getAttribute("data-always-show-toggle") === "true";
    const hasOverflow = maxVisible > 0 && links.length > maxVisible;

    if (btn) {
      btn.hidden = !hasOverflow && !alwaysShow;
      btn.addEventListener("click", () => {
        const expanded = root.dataset.expanded === "true";
        setExpanded(root, !expanded);
      });
    }

    setExpanded(root, false);
  }

  function initAll() {
    document.querySelectorAll(SELECTOR).forEach((el) => init(el));
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initAll);
  } else {
    initAll();
  }

  document.addEventListener("shopify:section:load", (e) => {
    const section = e.target;
    if (!section) return;
    section.querySelectorAll(SELECTOR).forEach((el) => {
      el.removeAttribute("data-browse-init");
      init(el);
    });
  });
})();
